import { ApplicationRef, Injectable, OnDestroy } from '@angular/core';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { BehaviorSubject, EMPTY, filter, map, Observable, Subscription, switchMap, takeUntil } from 'rxjs';
import { AuthService } from 'core-client/api';
import { OAuthService } from 'angular-oauth2-oidc';

/**
 * Connection error tracking interface
 */
interface ConnectionError {
  timestamp: number;
  message: string;
  count: number;
  lastLoggedAt: number;
}

export interface ISocketTokenAuth {
  permissionRef: string;
  userId: string
}

/**
 * Connection health states
 */
enum ConnectionHealthState {
  HEALTHY = 'HEALTHY',           // Normal operation - allow connections
  DEGRADED = 'DEGRADED',         // Too many failures - block connections
  RECOVERING = 'RECOVERING'      // Testing recovery - allow one attempt
}

/**
 * Connection health tracker
 */
interface ConnectionHealthTracker {
  state: ConnectionHealthState;
  failureCount: number;
  lastFailureTime: number;
  nextAttemptTime: number;
}

/**
 * Handler reference for tracking widget subscriptions
 */
interface HandlerReference {
  handlerId: string;
  dashboardId: string;  // Track which dashboard this handler belongs to
  subscriptions: Set<string>;
}

type SocketConnectionState = 'disconnected' | 'connecting' | 'connected';

/**
 *
 * Features:
 * - Full OAuth/Keycloak integration with token lifecycle management
 * - Intelligent backend auth rejection handling (distinguishes Keycloak restart vs invalid token)
 * - Dashboard-aware widget tracking with automatic cleanup
 * - Orphaned subscription cleanup for memory leak prevention
 * - Connection health tracking with circuit breaker pattern
 * - Error throttling and intelligent logging
 * - Plugin-aware connection control
 * - Network status monitoring with token sync
 *
 */
@Injectable({
  providedIn: 'root'
})
export class SocketIoService extends Socket implements OnDestroy {
  // Error tracking configuration
  private readonly ERROR_LOG_THROTTLE_MS: number = 30000;
  private readonly MAX_CONSECUTIVE_ERRORS: number = 3;

  // Connection health configuration
  private readonly HEALTH_FAILURE_THRESHOLD = 5;
  private readonly HEALTH_RECOVERY_TIMEOUT = 120000;  // 2 minutes

  // Error tracking
  private lastError?: ConnectionError;
  private errorCount: number = 0;
  private consecutiveErrorCount: number = 0;
  private errorLoggingSuppressed: boolean = false;

  // Handler management (widgets/components that need socket)
  private readonly registeredHandlers: Map<string, HandlerReference> = new Map();
  private readonly connectionState$: BehaviorSubject<SocketConnectionState> =
    new BehaviorSubject<SocketConnectionState>('disconnected');

  // Event subscriptions
  private readonly activeEventSubscriptions: Map<string, Observable<unknown>> = new Map();

  // Auth subscriptions
  private oauthEventsSubscription?: Subscription;
  private networkSubscription?: Subscription;

  // Token tracking
  private currentToken: string | null = null;

  // Connection health tracker
  private connectionHealthTracker: ConnectionHealthTracker = {
    state: ConnectionHealthState.HEALTHY,
    failureCount: 0,
    lastFailureTime: 0,
    nextAttemptTime: 0
  };

  // Plugin state - controlled by NgsildConnectorService
  private pluginEnabled: boolean = false;

  constructor(
    private readonly authService: AuthService,
    private readonly oauthService: OAuthService,
    appRef: ApplicationRef
  ) {
    // Initialize with current token
    const token = authService.getAccessToken();
    super(SocketIoService.createSocketConfig(token), appRef);
    this.currentToken = token;

    this.setupSocketEvents();
    this.setupAuthenticationHandling();
    this.setupNetworkHandling();
  }

  /**
   * Create socket configuration with authentication
   */
  private static createSocketConfig(token?: string | null): SocketIoConfig {
    return {
      url: `${document.location.protocol}//${document.location.hostname}:${document.location.port}`,
      options: {
        path: '/ngsild-proxy/api',
        autoConnect: false,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        randomizationFactor: 0.5,  // Add ±50% jitter to prevent thundering herd
        timeout: 20000,
        extraHeaders: token ? { 'Authorization': `Bearer ${token}` } : {},
        query: token ? { token } : {}
      }
    };
  }

  /**
   * Setup authentication handling with token lifecycle coordination
   */
  private setupAuthenticationHandling(): void {
    this.oauthEventsSubscription = this.oauthService.events.subscribe(event => {
      if (event.type === 'token_refreshed') {
        const newToken = this.authService.getAccessToken();

        if (this.registeredHandlers.size > 0) {
          console.log('[NGSILD Socket] Token refreshed, updating configuration');
        }

        // Update token for next connection
        this.currentToken = newToken;
        this.updateSocketConfig(newToken);

        // If we were stuck due to token issues and have handlers, try reconnecting
        if (!this.connected && this.registeredHandlers.size > 0) {
          console.log('[NGSILD Socket] Attempting reconnection with refreshed token');
          setTimeout(() => {
            this.ensureConnection();
          }, 1000);
        }
      }
      else if (event.type === 'token_received') {
        const newToken = this.authService.getAccessToken();
        if (this.registeredHandlers.size > 0) {
          console.log('[NGSILD Socket] Token received, ready for connection');
        }

        this.currentToken = newToken;
        this.updateSocketConfig(newToken);

        // If we have handlers waiting, connect now
        if (!this.connected && this.registeredHandlers.size > 0) {
          console.log('[NGSILD Socket] Connecting with new token');
          setTimeout(() => {
            this.ensureConnection();
          }, 500);
        }
      }
      else if (event.type === 'session_terminated' || event.type === 'session_error') {
        console.log('[NGSILD Socket] Session ended, clearing auth');
        this.handleSessionTerminated();
      }
    });

    // Handle auth errors from backend
    this.on('connect_error', (error: { message: string }) => {
      if (error.message === 'Authentication error' ||
        error.message === 'Unauthorized' ||
        error.message.includes('401') ||
        error.message.includes('403')) {
        console.error('[NGSILD Socket] Backend rejected authentication');
        this.handleBackendAuthRejection();
      }
    });

    this.on('auth_required', () => {
      console.warn('[NGSILD Socket] Backend requires authentication');
    });

    this.on('auth_invalid', () => {
      console.error('[NGSILD Socket] Backend rejected token');
      this.handleBackendAuthRejection();
    });
  }

  /**
   * Handle backend authentication rejection
   * Don't disconnect unnecessarily - Keycloak restart ≠ invalid token
   */
  private handleBackendAuthRejection(): void {
    const freshToken = this.authService.getAccessToken();
    const tokenChanged = freshToken !== this.currentToken;
    const providerAvailable = this.authService.isProviderAvailable();

    console.warn('[NGSILD Socket] Backend rejected authentication', {
      hasToken: !!freshToken,
      tokenChanged: tokenChanged,
      providerAvailable: providerAvailable
    });

    // No token at all - wait for token_received event
    if (!freshToken) {
      console.log('[NGSILD Socket] No token available, will reconnect when token received');

      if (this.connected) {
        this.disconnect();
      }

      this.connectionState$.next('disconnected');
      return;
    }

    // Token changed - reconnect with new token
    if (tokenChanged) {
      console.log('[NGSILD Socket] Token changed, reconnecting with fresh token');
      this.currentToken = freshToken;
      this.updateSocketConfig(freshToken);

      if (this.connected) {
        this.disconnect();
      }

      setTimeout(() => {
        this.ensureConnection();
      }, 1000);
      return;
    }

    // Same token, provider unavailable
    // Keep connection alive - backend will accept token when Keycloak returns
    if (!providerAvailable) {
      console.warn(
        '[NGSILD Socket] Auth provider unavailable ' +
        '(Keycloak restarting?) - keeping connection, backend will recover'
      );
      // No disconnect - Socket.IO will automatically retry
      // Backend will accept the token again when Keycloak comes back online
      return;
    }

    // Same token, provider available, backend still rejecting
    // Might be token revoked or temporary backend issue
    // Let Socket.IO handle reconnection, wait for token_refreshed event
    console.warn(
      '[NGSILD Socket] Backend rejecting token (might be revoked) - ' +
      'waiting for token refresh'
    );
    // No disconnect - wait for AuthService to refresh token
    // If token is revoked, token_refresh will fail and session will terminate
  }

  /**
   * Handle session termination (logout)
   */
  private handleSessionTerminated(): void {
    console.log('[NGSILD Socket] Session terminated, disconnecting');

    if (this.connected) {
      this.disconnect();
    }

    this.connectionState$.next('disconnected');
    this.currentToken = null;
    this.updateSocketConfig(null);
  }

  /**
   * Setup network status monitoring
   */
  private setupNetworkHandling(): void {
    this.networkSubscription = this.authService.isOnline$().subscribe(isOnline => {
      if (isOnline && this.registeredHandlers.size > 0) {
        console.log('[NGSILD Socket] Network reconnected, checking connection');

        const freshToken = this.authService.getAccessToken();

        if (freshToken !== this.currentToken) {
          console.log('[NGSILD Socket] Token changed during outage, updating');
          this.currentToken = freshToken;
          this.updateSocketConfig(freshToken);
        }

        // Reset connection health on network restore
        if (this.isConnectionDegraded()) {
          console.log('[NGSILD Socket] Network restored, resetting connection health');
          this.resetConnectionHealth();
        }

        if (!this.connected && this.connectionState$.value === 'disconnected') {
          console.log('[NGSILD Socket] Reconnecting after network restore');
          this.ensureConnection();
        }
      }
    });
  }

  /**
   * Update socket config with new token (for next connection)
   */
  private updateSocketConfig(token: string | null): void {
    if (token) {
      this.ioSocket.io.opts.extraHeaders = { 'Authorization': `Bearer ${token}` };
      this.ioSocket.io.opts.query = { token };
    } else {
      this.ioSocket.io.opts.extraHeaders = {};
      this.ioSocket.io.opts.query = {};
    }
  }

  /**
   * Setup socket events with connection health integration
   * Enhanced logging and recovery
   */
  private setupSocketEvents(): void {
    this.on('connect', (): void => {
      if (this.errorLoggingSuppressed || this.consecutiveErrorCount > 0) {
        console.log('%c[NGSILD Socket] Connected (recovered)',
          'color: #4CAF50; font-weight: bold');
      } else {
        console.log('[NGSILD Socket] Connected');
      }

      this.connectionState$.next('connected');
      this.resetErrorTracking();
      this.resetConnectionHealth();
    });

    this.on('disconnect', (reason: string): void => {
      console.log(`[NGSILD Socket] Disconnected: ${reason}`);
      this.connectionState$.next('disconnected');
    });

    this.on('connect_error', (err: { message: string }): void => {
      // Update connection health on failure
      this.markConnectionDegraded();

      // Auth errors handled in setupAuthenticationHandling()
      if (err.message === 'Authentication error' || err.message === 'Unauthorized') {
        return;
      }

      this.logConnectionError(err);
    });

    this.ioSocket.io.on('reconnect', (attemptNumber: number): void => {
      if (this.errorLoggingSuppressed) {
        console.log(`%c[NGSILD Socket] Reconnected after ${attemptNumber} attempt(s)`,
          'color: #4CAF50; font-weight: bold');
      } else {
        console.log(`[NGSILD Socket] Reconnected after ${attemptNumber} attempt(s)`);
      }

      this.resetErrorTracking();
      this.resetConnectionHealth();
    });

    this.ioSocket.io.on('reconnect_attempt', (attemptNumber: number): void => {
      if (attemptNumber === 1) {
        console.log('[NGSILD Socket] Attempting to reconnect...');
      } else if (attemptNumber % 10 === 0 && !this.errorLoggingSuppressed) {
        console.log(`[NGSILD Socket] Reconnection attempt #${attemptNumber}`);
      }
    });

    this.ioSocket.io.on('reconnect_failed', (): void => {
      console.error('[NGSILD Socket] All reconnection attempts exhausted');

      // Update connection health
      this.markConnectionDegraded();

      if (!this.errorLoggingSuppressed) {
        console.warn(
          '[NGSILD Socket] Cannot establish connection. ' +
          'Will retry when network/provider becomes available.'
        );
      }
    });

    this.ioSocket.io.on('error', (err: any): void => {
      const sanitizedMessage = this.sanitizeErrorMessage(err.message || err.toString());
      const errorType = this.getErrorType(sanitizedMessage);

      const now = Date.now();
      this.consecutiveErrorCount++;

      if (this.consecutiveErrorCount > this.MAX_CONSECUTIVE_ERRORS) {
        if (!this.errorLoggingSuppressed) {
          console.warn(
            `[NGSILD Socket] Suppressing errors (${errorType}). ` +
            `Will notify when restored.`
          );
          this.errorLoggingSuppressed = true;
        }
        return;
      }

      if (this.lastError?.message === sanitizedMessage) {
        this.lastError.count++;

        if (now - this.lastError.lastLoggedAt > this.ERROR_LOG_THROTTLE_MS) {
          console.error(
            `[NGSILD Socket] Error (${errorType}, ` +
            `${this.lastError.count}x): ${sanitizedMessage}`
          );
          this.lastError.lastLoggedAt = now;
        }
      } else {
        console.error(
          `[NGSILD Socket] Error (${errorType}): ${sanitizedMessage}`
        );

        this.lastError = {
          timestamp: now,
          message: sanitizedMessage,
          count: 1,
          lastLoggedAt: now
        };
      }
    });
  }

  /**
   * Set plugin enabled state (called by NgsildConnectorService)
   */
  setPluginEnabled(enabled: boolean): void {
    const wasEnabled = this.pluginEnabled;
    this.pluginEnabled = enabled;

    if (wasEnabled !== enabled) {
      console.log(`[NGSILD Socket] Plugin ${enabled ? 'enabled' : 'disabled'}`);
    }

    // If plugin was disabled and now enabled, try to connect
    if (!wasEnabled && enabled && this.registeredHandlers.size > 0) {
      console.log('[NGSILD Socket] Plugin enabled with active handlers, connecting');
      this.ensureConnection();
    }

    // If plugin was enabled and now disabled, disconnect
    if (wasEnabled && !enabled && this.connected) {
      console.log('[NGSILD Socket] Plugin disabled, disconnecting');
      this.disconnect();
      this.connectionState$.next('disconnected');
    }
  }

  /**
   * Set authentication token for socket connection
   * Legacy method for backward compatibility with custom permission tokens
   * OAuth tokens are now handled automatically via setupAuthenticationHandling()
   */
  setAuthToken(token: string): void {
    if (token) {
      this.ioSocket.auth = { token };
    } else {
      this.ioSocket.auth = {};
    }
  }

  /**
   * Check if connection is degraded
   */
  private isConnectionDegraded(): boolean {
    return this.connectionHealthTracker.state === ConnectionHealthState.DEGRADED;
  }

  /**
   * Check if connection attempt is allowed by health tracker
   */
  private canAttemptConnection(): boolean {
    const now = Date.now();

    switch (this.connectionHealthTracker.state) {
      case ConnectionHealthState.HEALTHY:
        // Normal operation - allow connection
        return true;

      case ConnectionHealthState.DEGRADED:
        // Too many failures - check if timeout has passed
        if (now >= this.connectionHealthTracker.nextAttemptTime) {
          console.log('[NGSILD Socket] Connection health check timeout elapsed, entering RECOVERING state');
          this.connectionHealthTracker.state = ConnectionHealthState.RECOVERING;
          return true;
        }
        return false;

      case ConnectionHealthState.RECOVERING:
        // Testing recovery - allow one attempt
        return true;

      default:
        return false;
    }
  }

  /**
   * Update connection health on connection failure
   */
  private markConnectionDegraded(): void {
    const now = Date.now();

    this.connectionHealthTracker.failureCount++;
    this.connectionHealthTracker.lastFailureTime = now;

    if (this.connectionHealthTracker.state === ConnectionHealthState.RECOVERING) {
      // Failed during testing - go back to DEGRADED
      console.warn('[NGSILD Socket] Connection health re-degraded after failed recovery test');
      this.connectionHealthTracker.state = ConnectionHealthState.DEGRADED;
      this.connectionHealthTracker.nextAttemptTime = now + this.HEALTH_RECOVERY_TIMEOUT;
      return;
    }

    // Check if we should mark connection as degraded
    if (this.connectionHealthTracker.failureCount >= this.HEALTH_FAILURE_THRESHOLD) {
      console.warn(
        `%c[NGSILD Socket] Connection marked as DEGRADED after ${this.connectionHealthTracker.failureCount} failures ` +
        `(will test connection in ${this.HEALTH_RECOVERY_TIMEOUT / 1000}s)`,
        'color: #FF5722; font-weight: bold'
      );

      this.connectionHealthTracker.state = ConnectionHealthState.DEGRADED;
      this.connectionHealthTracker.nextAttemptTime = now + this.HEALTH_RECOVERY_TIMEOUT;
    }
  }

  /**
   * Reset connection health on successful connection
   */
  private resetConnectionHealth(): void {
    if (this.connectionHealthTracker.failureCount > 0 ||
      this.connectionHealthTracker.state !== ConnectionHealthState.HEALTHY) {
      console.log('[NGSILD Socket] Connection health reset to HEALTHY');
    }

    this.connectionHealthTracker = {
      state: ConnectionHealthState.HEALTHY,
      failureCount: 0,
      lastFailureTime: 0,
      nextAttemptTime: 0
    };
  }

  /**
   * Reset error tracking after successful connection
   */
  private resetErrorTracking(): void {
    if (this.errorCount > 0) {
      console.log(`[NGSILD Socket] Connection restored (total errors during downtime: ${this.errorCount})`);
    }

    this.lastError = undefined;
    this.errorCount = 0;
    this.consecutiveErrorCount = 0;
    this.errorLoggingSuppressed = false;
  }

  /**
   * Log connection error with throttling
   */
  private logConnectionError(error: any): void {
    const now = Date.now();
    const sanitizedMessage = this.sanitizeErrorMessage(error.message || error.toString());
    const errorType = this.getErrorType(sanitizedMessage);

    this.errorCount++;
    this.consecutiveErrorCount++;

    if (this.consecutiveErrorCount > this.MAX_CONSECUTIVE_ERRORS) {
      if (!this.errorLoggingSuppressed) {
        console.warn(
          `[NGSILD Socket] Suppressing errors (${errorType}). ` +
          `Will notify when restored.`
        );
        this.errorLoggingSuppressed = true;
      }
      return;
    }

    if (this.lastError?.message === sanitizedMessage) {
      this.lastError.count++;

      if (now - this.lastError.lastLoggedAt > this.ERROR_LOG_THROTTLE_MS) {
        console.warn(
          `[NGSILD Socket] Connection error (${errorType}, ` +
          `${this.lastError.count}x): ${sanitizedMessage}`
        );
        this.lastError.lastLoggedAt = now;
      }
    } else {
      console.error(
        `[NGSILD Socket] Connection error (${errorType}): ${sanitizedMessage}`
      );

      this.lastError = {
        timestamp: now,
        message: sanitizedMessage,
        count: 1,
        lastLoggedAt: now
      };
    }

    if (this.consecutiveErrorCount === this.MAX_CONSECUTIVE_ERRORS) {
      console.info(
        '[NGSILD Socket] Will suppress further errors. ' +
        'Automatic reconnection active.'
      );
    }
  }

  /**
   * Sanitize error messages (remove sensitive data)
   */
  private sanitizeErrorMessage(message: string): string {
    return message
      .replace(/token=[^&\s]+/g, 'token=<redacted>')
      .replace(/Bearer\s+[^\s]+/g, 'Bearer <redacted>')
      .replace(/client_secret=[^&\s]+/g, 'client_secret=<redacted>');
  }

  /**
   * Extract error type from error message for better categorization
   */
  private getErrorType(message: string): string {
    if (message.includes('ECONNREFUSED')) return 'Connection Refused';
    if (message.includes('ETIMEDOUT')) return 'Timeout';
    if (message.includes('ENETUNREACH')) return 'Network Unreachable';
    if (message.includes('ENOTFOUND')) return 'Host Not Found';
    if (message.includes('timeout')) return 'Timeout';
    if (message.includes('403')) return 'Forbidden';
    if (message.includes('401')) return 'Unauthorized';
    return 'Connection Error';
  }

  /**
   * Register a handler that needs socket connection
   */
  registerHandler(handlerId: string, dashboardId: string): void {
    console.log(`[NGSILD Socket] Registering handler: ${handlerId} for dashboard: ${dashboardId}`);

    this.registeredHandlers.set(handlerId, {
      handlerId,
      dashboardId,
      subscriptions: new Set()
    });

    this.ensureConnection();
  }

  /**
   * Unregister a handler
   */
  unregisterHandler(handlerId: string): void {
    console.log(`[NGSILD Socket] Unregistering handler: ${handlerId}`);

    const handler = this.registeredHandlers.get(handlerId);
    if (handler) {
      handler.subscriptions.forEach((eventName: string) => {
        this.cleanupEventSubscription(eventName);
      });

      this.registeredHandlers.delete(handlerId);
    }

    this.checkConnectionNeed();
  }

  /**
   * Clean up handlers not on active dashboard
   */
  cleanupInactiveHandlers(activeDashboardId: string): void {
    console.log(`[NGSILD Socket] Cleaning inactive handlers for dashboard: ${activeDashboardId}`);

    const handlersToRemove: string[] = [];

    this.registeredHandlers.forEach((handler: HandlerReference, handlerId: string) => {
      if (handler.dashboardId !== activeDashboardId) {
        handlersToRemove.push(handlerId);
      }
    });

    handlersToRemove.forEach((handlerId: string) => {
      this.unregisterHandler(handlerId);
    });

    // Clean up orphaned event subscriptions
    this.cleanupOrphanedEventSubscriptions();
  }

  /**
   * Clean up event subscriptions not used by any handler
   */
  private cleanupOrphanedEventSubscriptions(): void {
    const eventsInUse = new Set<string>();

    this.registeredHandlers.forEach((handler: HandlerReference) => {
      handler.subscriptions.forEach((eventName: string) => {
        eventsInUse.add(eventName);
      });
    });

    // Remove subscriptions not used by any handler
    let removedCount = 0;
    this.activeEventSubscriptions.forEach((_, eventName: string) => {
      if (!eventsInUse.has(eventName)) {
        this.activeEventSubscriptions.delete(eventName);
        removedCount++;
      }
    });

    if (removedCount > 0) {
      console.log(
        `[NGSILD Socket] Cleaned up ${removedCount} orphaned event subscription(s)`
      );
    }
  }

  /**
   * Listen to server events
   */
  onEvent(eventName: string, handlerId: string): Observable<unknown> {
    if (!this.registeredHandlers.has(handlerId)) {
      console.warn(`[NGSILD Socket] Handler ${handlerId} not registered`);
      return EMPTY;
    }

    const handler = this.registeredHandlers.get(handlerId)!;
    handler.subscriptions.add(eventName);

    if (!this.activeEventSubscriptions.has(eventName)) {
      const eventObservable = this.connectionState$.pipe(
        filter((state: SocketConnectionState) => state === 'connected'),
        takeUntil(this.getDisconnectObservable()),
        switchMap(() => this.fromEvent(eventName))
      );
      this.activeEventSubscriptions.set(eventName, eventObservable);
    }

    return this.activeEventSubscriptions.get(eventName)!;
  }

  /**
   * Emit event to server
   */
  emitEvent(eventName: string, data: any, handlerId: string): void {
    if (!this.registeredHandlers.has(handlerId)) {
      console.warn(`[NGSILD Socket] Handler ${handlerId} not registered`);
      return;
    }

    if (this.connected) {
      this.emit(eventName, data);
    } else {
      console.warn(`[NGSILD Socket] Cannot emit ${eventName}, not connected`);
    }
  }

  /**
   * Get connection state observable
   */
  getConnectionState(): Observable<SocketConnectionState> {
    return this.connectionState$.asObservable();
  }

  /**
   * Get registered handlers
   */
  getRegisteredHandlers(): string[] {
    return Array.from(this.registeredHandlers.keys());
  }

  /**
   * Get connection health state for debugging
   */
  getConnectionHealthState(): ConnectionHealthTracker {
    return { ...this.connectionHealthTracker };
  }

  private getDisconnectObservable(): Observable<void> {
    return this.connectionState$.pipe(
      filter((state: SocketConnectionState) => state === 'disconnected'),
      map(() => void 0)
    );
  }

  private checkConnectionNeed(): void {
    const hasRegisteredHandlers = this.registeredHandlers.size > 0;

    if (!hasRegisteredHandlers && this.connected) {
      console.log('[NGSILD Socket] No handlers, disconnecting');
      this.disconnect();
      this.connectionState$.next('disconnected');
      this.cleanupAllEventSubscriptions();
    }
  }

  /**
   * Enhanced connection logic with health tracker, plugin awareness, and OAuth provider check
   */
  private ensureConnection(): void {
    const shouldConnect = this.registeredHandlers.size > 0 &&
      !this.connected &&
      this.connectionState$.value !== 'connecting';

    if (!shouldConnect) {
      return;
    }

    // Check if plugin is enabled - don't connect if disabled
    if (!this.pluginEnabled) {
      console.warn('[NGSILD Socket] Plugin disabled, deferring connection attempt');
      return;
    }

    // Check connection health - don't attempt if degraded or waiting for recovery
    if (!this.canAttemptConnection()) {
      const waitTime = Math.round((this.connectionHealthTracker.nextAttemptTime - Date.now()) / 1000);
      console.warn(
        `[NGSILD Socket] Connection health ${this.connectionHealthTracker.state}, ` +
        `deferring connection attempt (retry in ${waitTime}s)`
      );
      return;
    }

    // Check OAuth provider availability - don't attempt if Keycloak is down
    // This prevents connection attempts when we can't get/refresh tokens
    if (!this.authService.isProviderAvailable()) {
      console.warn('[NGSILD Socket] OAuth provider unavailable, deferring connection attempt');
      return;
    }

    // Validate token before attempting connection
    const token = this.authService.getAccessToken();

    if (!token) {
      console.warn('[NGSILD Socket] No valid token available yet, deferring connection');
      return;
    }

    // Update token if it changed
    if (token !== this.currentToken) {
      console.log('[NGSILD Socket] Updating token before connection');
      this.currentToken = token;
      this.updateSocketConfig(token);
    }

    console.log('[NGSILD Socket] Connecting...');
    this.connectionState$.next('connecting');
    this.connect();
  }

  /**
   * Optimized event subscription cleanup with early exit
   */
  private cleanupEventSubscription(eventName: string): void {
    // Early exit if event is still in use by any handler
    for (const handler of this.registeredHandlers.values()) {
      if (handler.subscriptions.has(eventName)) {
        return;
      }
    }

    // Only delete if no handler uses this event
    this.activeEventSubscriptions.delete(eventName);
  }

  private cleanupAllEventSubscriptions(): void {
    this.activeEventSubscriptions.clear();
  }

  forceCleanup(): void {
    console.log('[NGSILD Socket] Force cleanup');
    this.registeredHandlers.clear();
    this.cleanupAllEventSubscriptions();

    if (this.connected) {
      this.disconnect();
    }

    this.connectionState$.next('disconnected');
  }

  ngOnDestroy(): void {
    if (this.oauthEventsSubscription) {
      this.oauthEventsSubscription.unsubscribe();
    }

    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }

    this.forceCleanup();
    this.connectionState$.complete();
  }
}
